import { AlertCircle, Loader2, Plus, RotateCcw } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useReviewBoardCategorization } from "../hooks/useReviewBoardCategorization";
import { useReviewBoardLayout } from "../hooks/useReviewBoardLayout";
import { ReviewBoardColumn } from "./ReviewBoardColumn";

const COLLAPSIBLE_IDS = new Set(["ignored", "recentlyClosed"]);
const defaultWidthFor = (id) => (COLLAPSIBLE_IDS.has(id) ? 320 : 384);

export const ReviewBoard = ({
	pendingReviewMRs,
	myOpenMRs,
	recentlyClosedMRs,
	loading,
	error,
	sortOption,
	showReadyToMergeColumn = true,
	showApprovedPendingColumn = true,
	greyOutBlockingComments = true,
	enableCommentsExpansion = false,
	ignoredMRIds = [],
	onIgnoreMR,
	onUnignoreMR,
	onOpenDiff,
	searchQuery = "",
	getMRTags = () => [],
	allMRTags = [],
	onAddMRTag,
	onRemoveMRTag,
}) => {
	const { columns } = useReviewBoardCategorization(
		pendingReviewMRs,
		myOpenMRs,
		recentlyClosedMRs,
		ignoredMRIds,
		sortOption,
	);

	// Columns allowed by the global settings toggles (shared with Standard view).
	const availableColumns = useMemo(
		() =>
			columns
				.filter((c) => showReadyToMergeColumn || c.id !== "readyToMerge")
				.filter((c) => showApprovedPendingColumn || c.id !== "approvedBlocked"),
		[columns, showReadyToMergeColumn, showApprovedPendingColumn],
	);

	const columnMap = useMemo(() => {
		const map = new Map();
		for (const c of availableColumns) map.set(c.id, c);
		return map;
	}, [availableColumns]);

	const columnDefs = useMemo(
		() =>
			availableColumns.map((c) => ({
				id: c.id,
				defaultWidth: defaultWidthFor(c.id),
			})),
		[availableColumns],
	);

	const {
		order,
		hidden,
		widthFor,
		setColumnWidth,
		hideColumn,
		showColumn,
		moveColumn,
		resetLayout,
	} = useReviewBoardLayout(columnDefs);

	const orderedVisible = useMemo(
		() =>
			order
				.map((id) => columnMap.get(id))
				.filter(Boolean)
				.filter((c) => !hidden.includes(c.id)),
		[order, columnMap, hidden],
	);

	const hiddenColumns = useMemo(
		() =>
			order
				.map((id) => columnMap.get(id))
				.filter(Boolean)
				.filter((c) => hidden.includes(c.id)),
		[order, columnMap, hidden],
	);

	// Card drag state
	const [draggingMR, setDraggingMR] = useState(null);
	const [dragOverColumn, setDragOverColumn] = useState(null);

	// Column reorder state
	const [draggingColumnId, setDraggingColumnId] = useState(null);
	const [columnDragOverId, setColumnDragOverId] = useState(null);
	const [columnDropEdge, setColumnDropEdge] = useState("before");

	// Add-lane menu
	const [showAddMenu, setShowAddMenu] = useState(false);
	const addMenuRef = useRef(null);

	useEffect(() => {
		if (!showAddMenu) return;
		const handleClickOutside = (event) => {
			if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
				setShowAddMenu(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [showAddMenu]);

	const handleDragStart = (mr) => {
		setDraggingMR(mr);
	};

	const handleDragEnd = () => {
		setDraggingMR(null);
		setDragOverColumn(null);
	};

	const handleDragOver = (columnId) => {
		setDragOverColumn(columnId);
	};

	const handleDrop = (columnId, mrId) => {
		if (columnId === "ignored") {
			onIgnoreMR?.(draggingMR || mrId);
		} else if (draggingMR && ignoredMRIds.includes(draggingMR.id)) {
			// If dragging from ignored column to another column, unignore it
			onUnignoreMR?.(draggingMR || mrId);
		}
		setDraggingMR(null);
		setDragOverColumn(null);
	};

	const handleColumnDragStart = (columnId) => {
		setDraggingColumnId(columnId);
	};

	const handleColumnDragEnd = () => {
		setDraggingColumnId(null);
		setColumnDragOverId(null);
	};

	const handleColumnDragOver = (columnId, edge) => {
		setColumnDragOverId(columnId);
		if (edge) setColumnDropEdge(edge);
	};

	const handleColumnDrop = (targetColumnId) => {
		if (draggingColumnId && draggingColumnId !== targetColumnId) {
			moveColumn(draggingColumnId, targetColumnId, columnDropEdge);
		}
		setDraggingColumnId(null);
		setColumnDragOverId(null);
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-[calc(100vh-200px)]">
				<div className="text-center">
					<Loader2 className="w-12 h-12 mx-auto text-[var(--color-text-secondary)] animate-spin mb-4" />
					<p className="text-[var(--color-text-secondary)]">
						Loading merge requests...
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="w-full">
			{/* Error banner */}
			{error && (
				<div className="mx-4 mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg flex items-center gap-2 text-red-800 dark:text-red-200">
					<AlertCircle className="w-5 h-5 flex-shrink-0" />
					<span className="text-sm">{error}</span>
				</div>
			)}

			{/* Board toolbar */}
			<div className="flex items-center justify-end gap-2 px-4 pt-2">
				<div className="relative" ref={addMenuRef}>
					<button
						type="button"
						onClick={() => setShowAddMenu((prev) => !prev)}
						disabled={hiddenColumns.length === 0}
						className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
						title={
							hiddenColumns.length === 0
								? "No hidden lanes"
								: "Add a hidden lane"
						}
					>
						<Plus className="w-4 h-4" />
						Add lane
					</button>
					{showAddMenu && hiddenColumns.length > 0 && (
						<div className="absolute right-0 mt-1 z-30 min-w-[180px] bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg shadow-lg py-1">
							{hiddenColumns.map((c) => (
								<button
									key={c.id}
									type="button"
									onClick={() => {
										showColumn(c.id);
										setShowAddMenu(false);
									}}
									className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-bg-secondary)] flex items-center gap-2 text-[var(--color-text-primary)]"
								>
									<span
										className="w-2.5 h-2.5 rounded-full flex-shrink-0"
										style={{ backgroundColor: c.color }}
									/>
									{c.title}
								</button>
							))}
						</div>
					)}
				</div>
				<button
					type="button"
					onClick={resetLayout}
					className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] transition-colors"
					title="Reset lane order, sizes and visibility"
				>
					<RotateCcw className="w-4 h-4" />
					Reset layout
				</button>
			</div>

			{/* Kanban columns */}
			<div className="p-4 overflow-x-auto">
				<div className="flex gap-4 min-w-max">
					{orderedVisible.map((column) => (
						<ReviewBoardColumn
							key={column.id}
							column={column}
							width={widthFor(column.id)}
							onResize={setColumnWidth}
							onRemove={hideColumn}
							onDragStart={handleDragStart}
							onDragEnd={handleDragEnd}
							onDragOver={() => handleDragOver(column.id)}
							onDrop={(mrId) => handleDrop(column.id, mrId)}
							isDragTarget={dragOverColumn === column.id && draggingMR}
							draggingMR={draggingMR}
							collapsible={COLLAPSIBLE_IDS.has(column.id)}
							draggingFromIgnored={
								draggingMR && ignoredMRIds.includes(draggingMR.id)
							}
							greyOutBlockingComments={greyOutBlockingComments}
							enableCommentsExpansion={enableCommentsExpansion}
							draggingColumnId={draggingColumnId}
							isColumnDropTarget={
								columnDragOverId === column.id &&
								draggingColumnId &&
								draggingColumnId !== column.id
							}
							columnDropEdge={columnDropEdge}
							onColumnDragStart={handleColumnDragStart}
							onColumnDragEnd={handleColumnDragEnd}
							onColumnDragOver={handleColumnDragOver}
							onColumnDrop={handleColumnDrop}
							onOpenDiff={onOpenDiff}
							searchQuery={searchQuery}
							getMRTags={getMRTags}
							allMRTags={allMRTags}
							onAddMRTag={onAddMRTag}
							onRemoveMRTag={onRemoveMRTag}
						/>
					))}
				</div>
			</div>
		</div>
	);
};
